import { useEffect, useMemo, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import { Markdown } from "tiptap-markdown";
import {
  Bold,
  Check,
  ChevronDown,
  Code,
  Highlighter,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Strikethrough,
  Underline,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { HEADING_CLASS, HIGHLIGHT_COLORS, IMAGE_CLASS, MARK_CLASS } from "./richTextClasses";

interface Props {
  /** Markdown in… */
  value: string;
  /** …markdown out. The editing experience is WYSIWYG (no visible
   *  syntax), but the stored value stays plain markdown so RichText
   *  rendering, search, and tool-call payloads keep working. */
  onChange: (next: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  /** Editing-area minimum height (Tailwind class). */
  minHeightClass?: string;
  /** Known names for @mentions. Typing "@" opens a picker; selecting
   *  inserts plain "@Full Name" text (highlighted at render time by
   *  RichText's mentionNames pass). */
  mentionNames?: string[];
  /** Richer @mention options: show `label` (and `hint`) in the picker,
   *  insert "@" + `insert`. Takes precedence over mentionNames. */
  mentions?: MentionOption[];
  /** Called on Cmd/Ctrl+Enter — the submit shortcut for composers. */
  onModEnter?: () => void;
  /** Called on Escape (when no picker is open); the key stops there, so
   *  e.g. a surrounding drawer doesn't also close. */
  onEscape?: () => void;
  /** Called when focus leaves the editor (toolbar clicks don't count). */
  onBlur?: () => void;
  /** false = read-only: no toolbar, formatted text only. */
  editable?: boolean;
  /** Single newlines become line breaks (matches RichText rendering of
   *  values that started life as plain text). */
  breaks?: boolean;
  /** Accessible name for the editing area. */
  ariaLabel?: string;
  /** Turns on image paste / drop: uploads the file and returns the stored
   *  src (kept in the markdown) and alt text. Throw to skip the image. */
  onUploadImage?: (file: File) => Promise<{ src: string; alt: string }>;
  /** Maps a stored image src to what the browser loads; null = don't load
   *  it (shows the alt text). Default: as stored. */
  resolveImageSrc?: (src: string) => string | null;
}

const escAttr = (v: string) => v.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

/** Image files in a paste / drop (screenshots arrive as files or items). */
function imageFiles(data: DataTransfer | null): File[] {
  if (!data) return [];
  let files = Array.from(data.files);
  if (!files.length) {
    files = Array.from(data.items ?? [])
      .filter((i) => i.kind === "file")
      .map((i) => i.getAsFile())
      .filter((f): f is File => f !== null);
  }
  return files.filter((f) => f.type.startsWith("image/"));
}

export interface MentionOption {
  label: string;
  hint?: string;
  insert: string;
}

/** prosemirror-markdown escapes "_" etc.; keep @handles literal so the
 *  server (and renderers) can still resolve them. A handle is letters,
 *  digits and "._-", where a punctuation mark may arrive escaped ("\_").
 *  Only those escapes are undone: any other backslash (a literal one is
 *  saved as "\\") ends the handle and is left exactly as written. */
export const unescapeMentions = (md: string) =>
  md.replace(/@(?:[A-Za-z0-9._-]|\\[_.-])+/g, (m) => m.replace(/\\([_.-])/g, "$1"));

/**
 * True WYSIWYG editor (TipTap/ProseMirror): the author sees formatted
 * text — bold is bold, lists are lists — never markdown syntax.
 * Complements RichTextEditor (the markdown-source editor used by the
 * denser PMO panels); this one is for surfaces where the author thinks
 * "Word", not "GitHub".
 */
export function WysiwygEditor({
  value,
  onChange,
  placeholder,
  autoFocus = false,
  className,
  minHeightClass = "min-h-[10rem]",
  mentionNames,
  mentions,
  onModEnter,
  onEscape,
  onBlur,
  editable = true,
  breaks = false,
  ariaLabel,
  onUploadImage,
  resolveImageSrc,
}: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  // Mention picker state. The keydown handler lives inside TipTap's
  // editorProps (created once), so everything it needs goes through refs.
  const [mention, setMention] = useState<{
    query: string;
    from: number; // doc position of the "@"
    left: number;
    top: number;
  } | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const mentionRef = useRef(mention);
  mentionRef.current = mention;
  const options = useMemo<MentionOption[]>(
    () => mentions ?? (mentionNames ?? []).map((n) => ({ label: n, insert: n })),
    [mentions, mentionNames],
  );
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const suggestions = useMemo(() => {
    if (!mention || !options.length) return [];
    const q = mention.query.toLowerCase();
    return options
      .filter((o) => o.label.toLowerCase().includes(q) || o.insert.toLowerCase().startsWith(q))
      .slice(0, 6);
  }, [mention, options]);
  const suggestionsRef = useRef(suggestions);
  suggestionsRef.current = suggestions;
  const activeIdxRef = useRef(activeIdx);
  activeIdxRef.current = activeIdx;
  const onModEnterRef = useRef(onModEnter);
  onModEnterRef.current = onModEnter;
  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;
  const uploadRef = useRef(onUploadImage);
  uploadRef.current = onUploadImage;
  const resolveSrcRef = useRef(resolveImageSrc);
  resolveSrcRef.current = resolveImageSrc;
  const editorRef = useRef<Ed | null>(null);
  const [uploading, setUploading] = useState(0);
  // Inline images; the stored src stays in the node (and the markdown),
  // the browser loads the resolved one.
  // Drag a corner to resize (aspect ratio kept); the width is saved.
  const imageExt = useMemo(
    () =>
      Image.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            // Resolved per attribute so the resizable node view loads it too.
            src: {
              default: null,
              renderHTML: (attrs) => {
                const src = resolveSrcRef.current ? resolveSrcRef.current(attrs.src ?? "") : attrs.src;
                return src ? { src } : {};
              },
            },
            width: {
              default: null,
              parseHTML: (el) => (el.getAttribute("width") ? Number(el.getAttribute("width")) || null : null),
            },
            // Height follows the width (aspect ratio); never stored.
            height: { default: null, parseHTML: () => null, renderHTML: () => ({}) },
          };
        },
        addStorage() {
          return {
            markdown: {
              // Markdown has no image size: a resized image is saved as an
              // <img> tag with its width; others stay ![alt](src).
              serialize(state: any, node: any) {
                const { src, alt, width } = node.attrs;
                if (width) {
                  state.write(`<img src="${escAttr(src ?? "")}" alt="${escAttr(alt ?? "")}" width="${Math.round(width)}">`);
                } else {
                  // Escape backslashes as well as parens, or a "\" could break out.
                  state.write(`![${state.esc(alt ?? "")}](${String(src ?? "").replace(/[\\()]/g, "\\$&")})`);
                }
              },
              parse: {},
            },
          };
        },
      }).configure({
        inline: true,
        resize: {
          enabled: true,
          directions: ["top-left", "top-right", "bottom-left", "bottom-right"],
          minWidth: 48,
          minHeight: 24,
          alwaysPreserveAspectRatio: true,
        },
      }),
    [],
  );

  /** Upload images one by one, inserting each where it was pasted/dropped.
   *  A field that saves on blur gets a save once the last one lands. */
  const insertImages = async (files: File[], at: number) => {
    const upload = uploadRef.current;
    if (!upload) return;
    setUploading((n) => n + files.length);
    let pos = at;
    for (const file of files) {
      try {
        const { src, alt } = await upload(file);
        const ed = editorRef.current;
        if (!ed || ed.isDestroyed) return;
        pos = Math.min(pos, ed.state.doc.content.size);
        ed.chain().insertContentAt(pos, { type: "image", attrs: { src, alt } }).run();
        pos = ed.state.selection.to;
      } catch {
        // The uploader reports its own errors.
      } finally {
        setUploading((n) => n - 1);
      }
    }
    setTimeout(() => {
      if (editorRef.current && !editorRef.current.isFocused) onBlurRef.current?.();
    });
  };
  const insertImagesRef = useRef(insertImages);
  insertImagesRef.current = insertImages;
  const getMarkdown = (e: Ed) => unescapeMentions((e.storage as any).markdown.getMarkdown());

  const refreshMention = (e: Ed) => {
    if (!optionsRef.current.length) return;
    const { from, empty } = e.state.selection;
    if (!empty) {
      setMention(null);
      return;
    }
    const textBefore = e.state.doc.textBetween(
      Math.max(0, from - 40),
      from,
      "\n",
      "￼",
    );
    const m = /(^|[\s(])@([\w .'-]{0,30})$/.exec(textBefore);
    if (!m) {
      setMention(null);
      return;
    }
    const query = m[2];
    const atPos = from - query.length - 1;
    const caret = e.view.coordsAtPos(from);
    const wrap = wrapRef.current?.getBoundingClientRect();
    setMention({
      query,
      from: atPos,
      left: wrap ? caret.left - wrap.left : 0,
      top: wrap ? caret.bottom - wrap.top : 0,
    });
    setActiveIdx(0);
  };

  const editor = useEditor({
    extensions: [
      // Links: typed / pasted URLs autolink; Ctrl/Cmd+click opens one (a
      // plain click just places the cursor, since the editor is live).
      StarterKit.configure({
        link: {
          autolink: true,
          linkOnPaste: true,
          openOnClick: false,
          defaultProtocol: "https",
          HTMLAttributes: { target: "_blank", rel: "noopener noreferrer nofollow" },
        },
      }),
      // Yellow highlighter — draws the exec eye to specific text. Single
      // color on purpose: the stored value is markdown, and <mark>
      // round-trips through it cleanly where per-color spans would not.
      ColorHighlight,
      imageExt,
      // linkify: bare URLs already in the saved text load as links too.
      Markdown.configure({ transformPastedText: true, html: true, breaks, linkify: true }),
    ],
    content: value,
    editable,
    // Re-render on every change / cursor move so the toolbar's pressed
    // buttons follow the selection (Tiptap 3 doesn't by default).
    shouldRerenderOnTransaction: true,
    autofocus: autoFocus ? "end" : false,
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm max-w-none text-sm text-ink focus:outline-none",
          "prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:mb-1 prose-headings:mt-2",
          MARK_CLASS,
          HEADING_CLASS,
          IMAGE_CLASS,
          editable ? cn("px-3 py-2", minHeightClass) : "text-ink-2",
        ),
        ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
      },
      handlePaste: (view, event) => {
        const files = imageFiles(event.clipboardData);
        if (!files.length || !uploadRef.current) return false; // other files: the card's attachments
        event.preventDefault();
        void insertImagesRef.current(files, view.state.selection.from);
        return true;
      },
      handleDrop: (view, event, _slice, moved) => {
        const files = imageFiles(event.dataTransfer);
        if (moved || !files.length || !uploadRef.current) return false;
        event.preventDefault();
        const at = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos ?? view.state.selection.from;
        void insertImagesRef.current(files, at);
        return true;
      },
      handleClick: (_view, _pos, event) => {
        const a = (event.target as HTMLElement).closest?.("a[href]") as HTMLAnchorElement | null;
        if (!a || !(event.metaKey || event.ctrlKey)) return false;
        window.open(a.href, "_blank", "noopener,noreferrer");
        return true;
      },
      handleDoubleClickOn: (_view, _pos, node) => {
        if (node.type.name !== "image") return false;
        const src = resolveSrcRef.current ? resolveSrcRef.current(node.attrs.src ?? "") : node.attrs.src;
        if (src) window.open(src, "_blank", "noopener");
        return true;
      },
      handleKeyDown: (_view, event) => {
        if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
          if (onModEnterRef.current) {
            event.preventDefault();
            onModEnterRef.current();
            return true;
          }
          return false;
        }
        const men = mentionRef.current;
        const sug = suggestionsRef.current;
        if (event.key === "Escape" && onEscapeRef.current && !(men && sug.length)) {
          event.preventDefault();
          event.stopPropagation();
          onEscapeRef.current();
          return true;
        }
        if (!men || sug.length === 0) return false;
        if (event.key === "ArrowDown") {
          setActiveIdx((i) => (i + 1) % sug.length);
          return true;
        }
        if (event.key === "ArrowUp") {
          setActiveIdx((i) => (i - 1 + sug.length) % sug.length);
          return true;
        }
        if (event.key === "Enter" || event.key === "Tab") {
          pickMentionRef.current(sug[activeIdxRef.current]);
          return true;
        }
        if (event.key === "Escape") {
          setMention(null);
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: e }) => {
      onChange(getMarkdown(e));
      refreshMention(e);
    },
    onSelectionUpdate: ({ editor: e }) => refreshMention(e),
    onBlur: () => {
      // Let a click on the mention picker land first.
      setTimeout(() => setMention(null), 150);
      onBlurRef.current?.();
    },
  });

  editorRef.current = editor;

  const pickMention = (opt: MentionOption) => {
    const men = mentionRef.current;
    if (!editor || !men) return;
    editor
      .chain()
      .focus()
      .insertContentAt(
        { from: men.from, to: editor.state.selection.from },
        `@${opt.insert} `,
      )
      .run();
    setMention(null);
  };
  const pickMentionRef = useRef(pickMention);
  pickMentionRef.current = pickMention;

  // Keep the editor in sync if the upstream value changes identity
  // (e.g. a fresh fetch) while we're not the source of the change.
  useEffect(() => {
    if (!editor) return;
    const current = getMarkdown(editor);
    if (value !== current) {
      editor.commands.setContent(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  if (!editor) return null;

  if (!editable) {
    return editor.isEmpty ? null : <EditorContent editor={editor} className={className} />;
  }

  const btn = (active: boolean) =>
    cn(
      "rounded p-1.5 text-ink-muted transition-colors hover:bg-raised hover:text-ink",
      active && "bg-indigo-100 text-indigo-700 ring-1 ring-indigo-300 hover:bg-indigo-100 hover:text-indigo-700 dark:bg-indigo-500/25 dark:text-indigo-200 dark:ring-indigo-400/50 dark:hover:bg-indigo-500/25 dark:hover:text-indigo-200",
    );

  return (
    <div
      className={cn(
        "rounded-md border border-line bg-surface focus-within:border-brand-navy-400 focus-within:ring-1 focus-within:ring-brand-navy-300",
        className,
      )}
    >
      {/* mousedown preventDefault keeps focus (and the selection) in the
          editor, so a toolbar click never counts as a blur. */}
      <div
        className="flex flex-wrap items-center gap-0.5 border-b border-line-soft px-1.5 py-1"
        onMouseDown={(e) => e.preventDefault()}
      >
        {TOOLS.map((t, i) =>
          t === "|" ? (
            <span key={i} className="mx-1 h-4 w-px bg-muted" />
          ) : t === "heading" ? (
            <HeadingMenu key="heading" editor={editor} />
          ) : t === "link" ? (
            <LinkMenu key="link" editor={editor} btn={btn} />
          ) : t === "highlight" ? (
            <HighlightMenu key="highlight" editor={editor} btn={btn} />
          ) : (
            <button
              key={t.title}
              type="button"
              title={t.title}
              aria-label={t.title}
              aria-pressed={t.active(editor)}
              onClick={() => t.run(editor)}
              className={btn(t.active(editor))}
            >
              <t.icon className="h-3.5 w-3.5" />
            </button>
          ),
        )}
      </div>
      <div ref={wrapRef} className="relative">
        {/* Placeholder overlay — TipTap has no built-in placeholder
            without the extension; a pointer-inert overlay does the job. */}
        {placeholder && editor.isEmpty && (
          <span className="pointer-events-none absolute left-3 top-2 text-sm text-ink-subtle">
            {placeholder}
          </span>
        )}
        <EditorContent editor={editor} />
        {uploading > 0 && (
          <div role="status" className="border-t border-line-soft px-3 py-1 text-xs text-ink-muted">
            Uploading {uploading === 1 ? "image" : `${uploading} images`}…
          </div>
        )}
        {mention && suggestions.length > 0 && (
          <div
            className="absolute z-20 w-72 rounded-lg border border-line bg-surface py-1 shadow-lg"
            style={{ left: Math.min(mention.left, 320), top: mention.top + 2 }}
          >
            {suggestions.map((n, i) => (
              <button
                key={n.insert}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  pickMention(n);
                }}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-ink",
                  i === activeIdx ? "bg-raised" : "hover:bg-raised",
                )}
              >
                {n.hint ? (
                  <>
                    <span className="truncate">{n.label}</span>
                    <span className="truncate text-xs text-ink-muted">{n.hint}</span>
                  </>
                ) : (
                  <>@{n.label}</>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

type Ed = NonNullable<ReturnType<typeof useEditor>>;

type Tool = { title: string; icon: typeof Bold; active: (e: Ed) => boolean; run: (e: Ed) => void };

/** Highlight that keeps its color as data-color (no inline style), so the
 *  theme decides how each color looks; serialized as <mark data-color>. */
const ColorHighlight = Highlight.extend({
  addAttributes() {
    return {
      color: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-color"),
        renderHTML: (attrs) => (attrs.color ? { "data-color": attrs.color } : {}),
      },
    };
  },
});

/** A toolbar dropdown: closes on pick, Escape or a click outside. */
function useMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  return { open, setOpen, ref };
}

const MENU = "absolute left-0 top-full z-30 mt-1 rounded-lg border border-line bg-surface p-1 shadow-lg";

const STYLES = [
  { level: 0, label: "Normal text", short: "Normal", className: "text-sm" },
  { level: 1, label: "Heading 1", short: "H1", className: "text-lg font-semibold" },
  { level: 2, label: "Heading 2", short: "H2", className: "text-base font-semibold" },
  { level: 3, label: "Heading 3", short: "H3", className: "text-sm font-semibold" },
] as const;

function HeadingMenu({ editor }: { editor: Ed }) {
  const { open, setOpen, ref } = useMenu();
  const current = STYLES.find((s) => s.level && editor.isActive("heading", { level: s.level })) ?? STYLES[0];
  const pick = (level: number) => {
    const chain = editor.chain().focus();
    (level ? chain.setHeading({ level: level as 1 | 2 | 3 }) : chain.setParagraph()).run();
    setOpen(false);
  };
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        title="Text style"
        aria-label={`Text style: ${current.label}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex w-[4.5rem] items-center justify-between gap-1 rounded px-1.5 py-1 text-xs font-medium text-ink-muted hover:bg-raised hover:text-ink",
          current.level > 0 && "text-indigo-700 dark:text-indigo-200",
        )}
      >
        {current.short}
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div role="menu" className={cn(MENU, "w-40")}>
          {STYLES.map((s) => (
            <button
              key={s.level}
              type="button"
              role="menuitemradio"
              aria-checked={s.level === current.level}
              onClick={() => pick(s.level)}
              className={cn("flex w-full items-center justify-between rounded px-2 py-1 text-left text-ink hover:bg-raised", s.className)}
            >
              {s.label}
              {s.level === current.level && <Check className="h-3.5 w-3.5 text-indigo-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** The last highlight color picked (per page load); the main button reuses it. */
let lastHighlight: string | null = null;

/** Split button, like Docs / Word: the highlighter applies (or removes) the
 *  last color; the arrow opens the palette. */
function HighlightMenu({ editor, btn }: { editor: Ed; btn: (active: boolean) => string }) {
  const { open, setOpen, ref } = useMenu();
  const active = editor.isActive("highlight");
  const color = active ? (editor.getAttributes("highlight").color ?? null) : undefined;
  const pick = (name: string | null) => {
    lastHighlight = name;
    editor.chain().focus().setHighlight({ color: name } as { color: string }).run();
    setOpen(false);
  };
  const current = HIGHLIGHT_COLORS.find((c) => c.name === (active ? color : lastHighlight)) ?? HIGHLIGHT_COLORS[0];
  return (
    <div ref={ref} className="relative flex items-center">
      <button
        type="button"
        title="Highlight"
        aria-label="Highlight"
        aria-pressed={active}
        onClick={() =>
          active
            ? editor.chain().focus().unsetHighlight().run()
            : editor.chain().focus().setHighlight({ color: lastHighlight } as { color: string }).run()
        }
        className={cn(btn(active), "relative")}
      >
        <Highlighter className="h-3.5 w-3.5" />
        {/* The color it will apply. */}
        <span className={cn("absolute inset-x-1 bottom-0.5 h-0.5 rounded-full", current.swatch)} aria-hidden />
      </button>
      <button
        type="button"
        // Tooltip avoids the word "Highlight" so it's never mistaken for the main button.
        title="More colors"
        aria-label="Highlight color"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="rounded px-0.5 py-1.5 text-ink-muted transition-colors hover:bg-raised hover:text-ink"
      >
        <ChevronDown className="h-2.5 w-2.5" />
      </button>
      {open && (
        <div role="menu" aria-label="Highlight color" className={cn(MENU, "flex items-center gap-1")}>
          {HIGHLIGHT_COLORS.map((c) => (
            <button
              key={c.label}
              type="button"
              role="menuitemradio"
              aria-checked={color === c.name}
              title={c.label}
              aria-label={c.label}
              onClick={() => pick(c.name)}
              className={cn(
                "h-6 w-6 rounded border border-line-strong",
                c.swatch,
                color === c.name && "ring-2 ring-indigo-400 ring-offset-1 ring-offset-surface",
              )}
            />
          ))}
          <span className="mx-0.5 h-5 w-px bg-muted" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              editor.chain().focus().unsetHighlight().run();
              setOpen(false);
            }}
            className="rounded px-1.5 py-1 text-xs text-ink-muted hover:bg-raised hover:text-ink"
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}

/** Add / edit / remove a link on the selection (or the link under the cursor). */
function LinkMenu({ editor, btn }: { editor: Ed; btn: (active: boolean) => string }) {
  const { open, setOpen, ref } = useMenu();
  const active = editor.isActive("link");
  const [url, setUrl] = useState("");
  const show = () => {
    setUrl(editor.getAttributes("link").href ?? "");
    setOpen((o) => !o);
  };
  const apply = () => {
    const href = url.trim();
    const chain = editor.chain().focus().extendMarkRange("link");
    if (!href) chain.unsetLink().run();
    else {
      const full = /^(https?:|mailto:)/i.test(href) ? href : `https://${href}`;
      // No selection and not on a link: insert the URL itself as the text.
      if (editor.state.selection.empty && !active) {
        chain.insertContent({ type: "text", text: full, marks: [{ type: "link", attrs: { href: full } }] }).run();
      } else chain.setLink({ href: full }).run();
    }
    setOpen(false);
  };
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        title="Link (Ctrl+click a link to open it)"
        aria-label="Link"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-pressed={active}
        onClick={show}
        className={btn(active)}
      >
        <LinkIcon className="h-3.5 w-3.5" />
      </button>
      {open && (
        // The toolbar keeps focus in the editor on mousedown; the input needs it.
        <div className={cn(MENU, "flex w-72 items-center gap-1")} onMouseDown={(e) => e.stopPropagation()}>
          <input
            autoFocus
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                apply();
              }
            }}
            placeholder="https://…"
            aria-label="Link URL"
            className="min-w-0 flex-1 rounded border border-line-strong bg-surface px-2 py-1 text-xs text-ink outline-none focus:border-indigo-400"
          />
          <button type="button" onClick={apply} className="rounded bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-500">
            {active ? "Update" : "Add"}
          </button>
          {active && (
            <button
              type="button"
              onClick={() => {
                editor.chain().focus().extendMarkRange("link").unsetLink().run();
                setOpen(false);
              }}
              className="rounded px-1.5 py-1 text-xs text-ink-muted hover:bg-raised hover:text-ink"
            >
              Remove
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const TOOLS: (Tool | "|" | "heading" | "highlight" | "link")[] = [
  { title: "Bold (Ctrl+B)", icon: Bold, active: (e) => e.isActive("bold"), run: (e) => e.chain().focus().toggleBold().run() },
  { title: "Italic (Ctrl+I)", icon: Italic, active: (e) => e.isActive("italic"), run: (e) => e.chain().focus().toggleItalic().run() },
  { title: "Underline (Ctrl+U)", icon: Underline, active: (e) => e.isActive("underline"), run: (e) => e.chain().focus().toggleUnderline().run() },
  { title: "Strikethrough", icon: Strikethrough, active: (e) => e.isActive("strike"), run: (e) => e.chain().focus().toggleStrike().run() },
  "highlight",
  "|",
  "heading",
  { title: "Bulleted list", icon: List, active: (e) => e.isActive("bulletList"), run: (e) => e.chain().focus().toggleBulletList().run() },
  { title: "Numbered list", icon: ListOrdered, active: (e) => e.isActive("orderedList"), run: (e) => e.chain().focus().toggleOrderedList().run() },
  "link",
  { title: "Quote", icon: Quote, active: (e) => e.isActive("blockquote"), run: (e) => e.chain().focus().toggleBlockquote().run() },
  // A block (inline code: type `text` between backticks).
  { title: "Code block", icon: Code, active: (e) => e.isActive("codeBlock"), run: (e) => e.chain().focus().toggleCodeBlock().run() },
];
